import { cloneDeep } from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { RatingContext } from './qualityAnalysis'
import { AssetTreeContext, ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import { getDeviations } from '../qualityAnalysis/analysisUtils'

interface IProps {
    ratingType: keyof RatingInfo
    TempConfig: RatingType
    setTempConfig: (newConfig: RatingType) => void
}
export function DeviationDialogContent({ ratingType, TempConfig, setTempConfig }: IProps): JSX.Element {
    const { packagers } = useContext(RatingContext)
    const [CurrentPackager, setCurrentPackager] = useState(packagers[0])
    const [deviations, setDeviations] = useState<Deviations>({
        packaging: [],
        appearance: [],
        texture: [],
        taste: []
    })

    const assetTree = useContext(AssetTreeContext)
    const urlParams = useContext(ParamsContext).split('/')

    useEffect(() => {
        setDeviations(getDeviations(urlParams, assetTree))
    }, [])

    function onClickDeviation(clickedDeviation: string): void {
        const newConfigs = cloneDeep(TempConfig)
        const newPackagerDeviations = newConfigs.deviations[CurrentPackager] || new Set()
        const allDeviations = newConfigs.deviations.allDeviations

        if (ratingType === 'packaging') {
            if (newPackagerDeviations.has(clickedDeviation)) newPackagerDeviations.delete(clickedDeviation)
            else newPackagerDeviations.add(clickedDeviation)
            newConfigs.deviations[CurrentPackager] = newPackagerDeviations
        }

        if (allDeviations.has(clickedDeviation)) allDeviations.delete(clickedDeviation)
        else allDeviations.add(clickedDeviation)

        setTempConfig(newConfigs)
    }

    return (
        <StyledDeviationDialogContent className={'deviationDialogContent'}>
            {ratingType === 'packaging' ? (
                <div className="deviationDialogContent--tabs">
                    {packagers.map(packager => (
                        <h3
                            className={`deviationDialogContent--tabs__tab ${
                                packager === CurrentPackager ? 'activeTab' : ''
                            }`}
                            key={packager}
                            onClick={() => setCurrentPackager(packager)}
                        >
                            {packager}
                            <span className={`deviationDialogContent--tabs__tab--deviationCount `}>
                                {TempConfig.deviations[packager]?.size || 0}
                            </span>
                        </h3>
                    ))}
                </div>
            ) : null}
            <div className="deviationDialogContent--deviations">
                {deviations[ratingType].map((deviation: string) => {
                    let isChecked
                    if (ratingType === 'packaging') {
                        isChecked = TempConfig.deviations[CurrentPackager]?.has(deviation)
                    } else {
                        isChecked = TempConfig.deviations.allDeviations?.has(deviation)
                    }

                    return (
                        <label
                            className="deviationDialogContent--deviations__deviation"
                            key={deviation.replaceAll(' ', '')}
                        >
                            <input
                                className="deviationDialogContent--deviations__deviation--input"
                                type="checkbox"
                                name={deviation}
                                onChange={() => onClickDeviation(deviation)}
                                aria-checked={isChecked}
                            />
                            <span
                                className={`deviationDialogContent--deviations__deviation--styledInput ${
                                    isChecked ? 'isChecked' : ''
                                }`}
                            >
                                {isChecked ? (
                                    <i className="fas fa-check-square"></i>
                                ) : (
                                    <i className="far fa-square"></i>
                                )}
                            </span>
                            {deviation}
                        </label>
                    )
                })}
            </div>
        </StyledDeviationDialogContent>
    )
}

const StyledDeviationDialogContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    --paddingSides: 24px;
    .deviationDialogContent {
        &--tabs {
            width: 100%;
            display: flex;
            background: ${({ theme }) => theme['gray-300']};

            &__tab {
                display: flex;
                align-items: center;
                padding: 16px var(--paddingSides);
                cursor: pointer;
                background: ${({ theme }) => theme['gray-300']};
                text-transform: capitalize;
                &.activeTab {
                    background: ${({ theme }) => theme['gray-100']};
                }
                &--deviationCount {
                    border-radius: 3px;
                    font-size: 14px;
                    margin-left: 5px;
                    padding: 2px 4px;
                    font-family: ${({ theme }) => theme['font-bold']};
                    background: ${({ theme }) => theme['blue-500']};
                    color: ${({ theme }) => theme['gray-0']};
                }
            }
        }
        &--deviations {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
            grid-template-rows: auto;
            gap: 10px 0px;
            width: 100%;
            padding: var(--paddingSides);
            &__deviation {
                display: flex;
                font-size: 13px;
                align-items: center;
                text-transform: capitalize;
                position: relative;
                cursor: pointer;
                user-select: none;

                /* Hide the browser's default checkbox */
                &--input {
                    position: absolute;
                    opacity: 0;
                    pointer-events: none;
                }

                /* Create a custom checkbox */
                &--styledInput {
                    font-size: 20px;
                    color: ${({ theme }) => theme['gray-400']};
                    margin-right: 8px;
                    &.isChecked {
                        color: ${({ theme }) => theme['blue-500']};
                    }
                }

                /* On mouse-over, add a grey background color */
                &:hover {
                    i {
                        color: ${({ theme }) => theme['blue-500']};
                    }
                }
            }
        }
    }
`

const DEVIATIONS = {
    packaging: [
        'aparência da solda horizontal',
        'aparência da solda vertical',
        'bolsa mordida',
        'centralização horizontal',
        'código falho',
        'corte horizontal',
        'defeitos de impressão',
        'delaminação',
        'dobra',
        'emendas',
        'estrias',
        'fita promocional desalinhada',
        'fora de passo',
        'macrofuro',
        'marcas de correia',
        'posição do código',
        'rompimento',
        'selagem fraca',
        'selagem vertical descentralizada',
        'strip paper'
    ],
    appearance: [
        'aparência oleosa',
        'bolhas',
        'coloração (claro)',
        'coloração (escuro)',
        'coloração (variação)',
        'comprimento',
        'cor indesejável',
        'curvados',
        'dano por inseto',
        'dano seco',
        'defeitos',
        'defeitos de batata',
        'diâmetro',
        'dobrados',
        'espessura',
        'excesso de aroma',
        'excesso de pele',
        'expansão',
        'flat',
        'flocos escuros',
        'formato',
        'grudados',
        'largura',
        'oleoso',
        'pele',
        'polpa amarela',
        'pontos brancos',
        'pontos duros',
        'pontos tostados',
        'poroso',
        'proporção do mix',
        'quebrados',
        'queimado',
        'rebarbas',
        'recobrimento',
        'resíduo de forno',
        'rugoso',
        'scraps',
        'variação collet',
        'variedade do milho',
        'verdes'
    ],
    texture: [
        'aerado',
        'cru',
        'difícil deglutição',
        'duro / vítrio',
        'gritz de milho',
        'murcho',
        'pontos duros',
        'poroso',
        'seco',
        'soft',
        'variação de textura'
    ],
    taste: [
        'amargo',
        'amendoim',
        'azedo',
        'residual de base',
        'sabor alterado',
        'sabor de óleo',
        'sabor de queimado',
        'sal alto',
        'sal baixo',
        'variação de sabor',
        'variação de sal'
    ]
} as const
